import { Events, GridApi } from '@ag-grid-community/core'
import { Injectable } from '@angular/core'
import _ from 'lodash-es'
import { Subject } from 'rxjs'
import {
  RevertFilterAdvancedFilterState,
  RevertFilterState,
  createRevertFilterState,
} from '../../utils/revert-filter-utils'

const REVERT_FILTER_STACK_LIMIT = 10
const FILTER_CHANGED_DEBOUNCE_TIME = 300

/**
 * Usage:
 *
 * Since the revert filter service shouldn't have knowledge of the other
 * panels or grids, this is scoped to specifically the grid component.
 *
 * Example:
 *
 * @ Component({
 * ...
 *  providers: [RevertFilterService]
 * })
 *
 */
@Injectable()
export class RevertFilterService {
  /** @internal */
  readonly _previousFilterStateStack: RevertFilterState[] = []
  /** @internal */
  _currentFilterState: RevertFilterState = createRevertFilterState()
  /** @internal */
  _gridApi?: GridApi
  /** @internal */
  _isReverting = false

  readonly advancedFiltersChanged$ =
    new Subject<RevertFilterAdvancedFilterState>()

  readonly onRevertFilters$ = new Subject<RevertFilterState>()

  disableRevert = true

  /**
   * @internal
   */
  readonly filterChangedListener = _.debounce(() => {
    if (this._isReverting) {
      this._isReverting = false
      return
    }

    const filterModel = this._gridApi?.getFilterModel() ?? {}

    // If same as the previous model, skip
    if (_.isEqual(filterModel, this._currentFilterState.grid)) {
      return
    }

    const currentFilterState = this._currentFilterState
    this._currentFilterState = {
      grid: filterModel,
      advancedFilters: currentFilterState.advancedFilters,
    }
    this._previousFilterStateStack.push(currentFilterState)

    // If the stack is greater than 10, remove the oldest
    if (this._previousFilterStateStack.length > REVERT_FILTER_STACK_LIMIT) {
      this._previousFilterStateStack.shift()
    }

    this.setDisableRevert()
  }, FILTER_CHANGED_DEBOUNCE_TIME)

  setupRevertFilter(gridApi?: GridApi) {
    this._gridApi = gridApi
    this._gridApi?.addEventListener(
      Events.EVENT_FILTER_CHANGED,
      this.filterChangedListener,
    )
  }

  destroy() {
    this._gridApi?.removeEventListener(
      Events.EVENT_FILTER_CHANGED,
      this.filterChangedListener,
    )
  }

  revertFilters() {
    if (this._previousFilterStateStack.length === 0) {
      return
    }

    this._isReverting = true
    const previousFilterState = this._previousFilterStateStack.pop()
    this._gridApi?.setFilterModel(previousFilterState?.grid)
    this.advancedFiltersChanged$.next(previousFilterState?.advancedFilters)
    this.setDisableRevert()
    this.onRevertFilters$.next(previousFilterState)
  }

  advancedFiltersChanged<T>(advancedFilters: T) {
    // Skip if advanced filters are the same
    if (_.isEqual(advancedFilters, this._currentFilterState.advancedFilters)) {
      return
    }

    const currentFilterState = this._currentFilterState
    this._currentFilterState = {
      grid: currentFilterState.grid,
      advancedFilters: advancedFilters as RevertFilterAdvancedFilterState,
    }

    this._previousFilterStateStack.push(currentFilterState)
    this.setDisableRevert()
  }

  /**
   * @internal
   */
  setDisableRevert() {
    this.disableRevert = this._previousFilterStateStack.length === 0
  }
}
